import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { MessageCircle, Sparkles } from 'lucide-react';
import { fetchCrystals, resolveImageUrl } from '../services/api';
import { Fireflies, Moth } from '../components/FairyElements';

const Crystals = () => {
  const whatsappNumber = '9681424041';
  const whatsappLink = `https://wa.me/91${whatsappNumber}`;
  const [crystalProducts, setCrystalProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCrystals().then(setCrystalProducts).catch(() => setCrystalProducts([])).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#0a1612]">
      <section className="forest-bg min-h-[500px] flex items-center px-4 md:px-8 lg:px-16 py-20" style={{ '--forest-image': "url(/mystical-garden/crystals-cave.png)", '--bg-opacity': 0.6}}>
        <Fireflies count={40} />
        <Moth delay={0} top="30%" />

        <div className="max-w-6xl mx-auto w-full text-center relative z-10 animate-fadeIn">
          <Badge className="mb-4 bg-emerald-900/60 border border-amber-500/40 text-amber-300 backdrop-blur-md">
            <Sparkles className="w-3 h-3 mr-1.5" />
            <span className="tracking-widest text-xs uppercase">Healing Crystals</span>
          </Badge>
          <h1 className="text-6xl md:text-7xl font-bold mb-6 gradient-text text-glow">Crystal Collection</h1>
          <p className="text-lg md:text-xl text-amber-100/80 max-w-3xl mx-auto font-serif-mystical italic">
            Each stone carefully selected from the mystical earth to bring positive energy, protection, and transformation into your life.
          </p>
        </div>
      </section>

      <section className="section-deep py-24 px-4 md:px-8 lg:px-16 relative">
        <Fireflies count={30} />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {loading ? (
              <p className="text-amber-100/60 col-span-full text-center py-12">Loading crystals...</p>
            ) : crystalProducts.length === 0 ? (
              <p className="text-amber-100/60 col-span-full text-center py-12">No crystals available yet.</p>
            ) : crystalProducts.map((crystal) => (
              <Card key={crystal.id} className="forest-card border-0 overflow-hidden group" data-testid={`crystal-card-${crystal.id}`}>
                <div className="h-64 overflow-hidden bg-emerald-900/40 relative">
                  <img
                    src={resolveImageUrl(crystal.image)}
                    alt={crystal.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 opacity-90"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-emerald-950 via-transparent to-transparent" />
                </div>

                <CardHeader>
                  <Badge className="mb-3 w-fit bg-emerald-800/80 text-amber-300 border border-amber-500/30">{crystal.category}</Badge>
                  <CardTitle className="text-2xl text-amber-200 group-hover:text-amber-300 transition-colors" style={{ fontFamily: 'Cinzel, serif' }}>
                    {crystal.name}
                  </CardTitle>
                  <CardDescription className="text-amber-100/70 leading-relaxed">{crystal.description}</CardDescription>
                </CardHeader>

                <CardContent>
                  <div className="mb-4">
                    <h4 className="font-semibold text-amber-300 mb-2 uppercase tracking-wider text-xs" style={{ fontFamily: 'Cinzel, serif' }}>
                      Benefits
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {crystal.benefits.map((benefit, idx) => (
                        <Badge key={idx} className="bg-transparent border border-emerald-600/40 text-amber-100/80">
                          {benefit}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <Button className="btn-gold w-full" onClick={() => window.open(whatsappLink, '_blank')}>
                    <MessageCircle className="mr-2 h-4 w-4" />
                    Book via WhatsApp
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="forest-bg py-24 px-4 md:px-8 lg:px-16 relative" style={{ '--forest-image': "url(/mystical-garden/crystals-cave.png)", '--bg-opacity': 0.3}}>
        <Fireflies count={20} />
        <div className="max-w-5xl mx-auto relative z-10">
          <h2 className="text-5xl font-bold text-center mb-16 gradient-text">Crystal Care Guide</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="forest-card border-0">
              <CardHeader>
                <CardTitle className="text-xl text-amber-200" style={{ fontFamily: 'Cinzel, serif' }}>Cleansing Your Crystals</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-amber-100/80">
                  <li>• Rinse under running water (avoid for soft stones)</li>
                  <li>• Place in moonlight overnight during full moon</li>
                  <li>• Use sage smoke or incense</li>
                  <li>• Bury in salt or earth for 24 hours</li>
                  <li>• Use sound vibrations from singing bowls</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="forest-card border-0">
              <CardHeader>
                <CardTitle className="text-xl text-amber-200" style={{ fontFamily: 'Cinzel, serif' }}>Charging Your Crystals</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-amber-100/80">
                  <li>• Place in sunlight for 4-6 hours (some crystals)</li>
                  <li>• Moonlight charging during full moon nights</li>
                  <li>• Use other crystals like selenite or clear quartz</li>
                  <li>• Set intentions while holding the crystal</li>
                  <li>• Place on crystal clusters or geodes</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="forest-bg py-24 px-4 md:px-8 lg:px-16 relative" style={{ '--forest-image': "url(/mystical-garden/hero-forest.png)", '--bg-opacity': 0.5}}>
        <Fireflies count={40} />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">Need Help Choosing?</h2>
          <p className="text-lg mb-8 text-amber-100/80 font-serif-mystical italic">
            Contact me for personalized crystal recommendations based on your specific needs and intentions.
          </p>
          <Button size="lg" className="btn-gold px-10 py-7" onClick={() => window.open(whatsappLink, '_blank')}>
            <MessageCircle className="mr-2 h-5 w-5" />
            Get Crystal Guidance
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Crystals;
